'use strict';
var isPlainObject = require('lodash/isPlainObject');
var isFunction = require('lodash/isFunction');
var _find = require('lodash/find');
var _extend = require('lodash/extend');
var _transform = require('lodash/transform');
var _merge = require('lodash/merge');
var _get = require('lodash/get');
var _set = require('lodash/set');
var Inflector = require('./inflector');

module.exports = function (jsonapi, data, opts) {
  var alreadyIncluded;

  function isComplexType(obj) {
    return Array.isArray(obj) || isPlainObject(obj);
  }

  function getValueForRelationship(relationshipData, included) {
    if (opts && relationshipData && opts[relationshipData.type]) {
      var valueForRelationshipFct = opts[relationshipData.type]
        .valueForRelationship;

      return valueForRelationshipFct(relationshipData, included);
    } else {
      return included;
    }
  }

  function findIncluded(relationshipData, relationshipName, from) {
    return new Promise(function (resolve) {
      if (!relationshipData) { return resolve(null); }

      var included = _find(jsonapi.included, {
        id: relationshipData.id,
        type: relationshipData.type
      });
      included = included || {
        id: relationshipData.id,
        type: relationshipData.type
      }

      var path = [
        from.type,
        from.id,
        relationshipName,
        relationshipData.type,
        relationshipData.id,
      ].join('_');

      // Check if the include is already processed (prevent circular references).
      if (alreadyIncluded[path]) return resolve(null);
      alreadyIncluded[path] = true;

      if (included) {
        return Promise
          .all([extractAttributes(included), extractRelationships(included)])
          .then(function (results) {
            var attributes = results[0];
            var relationships = results[1];
            resolve(_extend(attributes, relationships));
          });
      } else {
        return resolve(null);
      }
    });
  }

  function keyForAttribute(attribute) {
    if (isPlainObject(attribute)) {
      return _transform(attribute, function (result, value, key) {
        if (isComplexType(value)) {
          result[keyForAttribute(key)] = keyForAttribute(value);
        } else {
          result[keyForAttribute(key)] = value;
        }
      });
    } else if (Array.isArray(attribute)) {
      return attribute.map(function (attr) {
        if (isComplexType(attr)) {
          return keyForAttribute(attr);
        } else {
          return attr;
        }
      });
    } else {
      if (isFunction(opts.keyForAttribute)) {
        return opts.keyForAttribute(attribute);
      } else {
        return Inflector.caserize(attribute, opts);
      }
    }
  }

  function extractAttributes(from) {
    var dest = keyForAttribute(from.attributes || {});
    if ('id' in from) { dest[opts.id || 'id'] = from.id; }

    if (opts.typeAsAttribute) {
      if ('type' in from) { dest.type = from.type; }
    }
    if ('meta' in from) { dest.meta = keyForAttribute(from.meta || {}) }

    return dest;
  }

  function extractRelationships(from) {
    if (!from.relationships) { return; }

    var dest = {};

    return Promise
      .all(Object.keys(from.relationships).map(function (key) {
        var relationship = from.relationships[key];

        if (relationship.data === null) {
          dest[keyForAttribute(key)] = null;
        } else if (Array.isArray(relationship.data)) {
          return Promise
            .all(relationship.data.map(function (relationshipData) {
              return extractIncludes(relationshipData, key, from);
            }))
            .then(function (includes) {
              if (includes) { dest[keyForAttribute(key)] = includes; }
            });
        } else {
          return extractIncludes(relationship.data, key, from)
            .then(function (include) {
              if (include) { dest[keyForAttribute(key)] = include; }
            });
        }
      }))
      .then(function() {
        return dest;
      });
  }

  function extractLinks(from) {
    return keyForAttribute(from.links);
  }

  function extractIncludes(relationshipData, relationshipName, from) {
    return findIncluded(relationshipData, relationshipName, from)
      .then(function (included) {
        var valueForRelationship = getValueForRelationship(relationshipData,
          included);

        if (valueForRelationship && isFunction(valueForRelationship.then)) {
          return valueForRelationship.then(function (value) {
            return value;
          });
        } else {
          return valueForRelationship;
        }
      });
  }

  function deserializeCollection (array) {
    var collection = array.map(function (data) {
      return deserializeRecord(data)
    })
    return Promise.all(collection).then(function (result) {
      if (jsonapi.links) {
        result.links = keyForAttribute(jsonapi.links);
      }
      if (jsonapi.meta) {
        result.meta = keyForAttribute(jsonapi.meta);
      }
      return result;
    });
  }

  function deserializeRecord (data) {
    alreadyIncluded = {};

    var thingsToBeExtracted = [
      extractAttributes(data),
      extractRelationships(data)
    ];

    if (isPlainObject(data.links)) {
      thingsToBeExtracted.push(extractLinks(data));
    }

    return Promise
      .all(thingsToBeExtracted)
      .then(function (results) {
        var attributes = results[0];
        var relationships = results[1];
        if (results[2]) {
          var links = results[2];
        }
        var record = _extend(attributes, relationships);

        // Links
        if (links) {
          record.links = links;
        }

        // If option is present, transform record
        if (opts && opts.transform) {
          record = opts.transform(record);
        }

        return record;
      });
  }

  this.perform = function () {
    if (Array.isArray(jsonapi.data)) {
      return deserializeCollection(jsonapi.data);
    }
    return deserializeRecord(jsonapi.data);
  };
};
